import { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Alert, Stack, Table, Badge } from "react-bootstrap";
import { HttpData, HttpNoData } from "../../../../Core";
import {
    getProductSizeRangeMappingsBySource,
    createProductSizeRangeMapping,
    deleteProductSizeRangeMapping,
} from "../../Page/apiForNewProducts";

export default function ProductMappingsModal({
    show,
    onHide,
    jwt,
    sourceProductId,
    sourceType,
    onMappingChanged,
}) {
    const [currentMapping, setCurrentMapping] = useState(null);
    const [allNewProducts, setAllNewProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Автокомплит для основной привязки
    const [searchTerm, setSearchTerm] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedNewProduct, setSelectedNewProduct] = useState(null);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    // Диапазоны
    const [ranges, setRanges] = useState([]);
    const [showAddRange, setShowAddRange] = useState(false);
    const [rangeForm, setRangeForm] = useState({ sizeFrom: "", sizeTo: "", newProductId: null });
    const [rangeSearch, setRangeSearch] = useState("");
    const [showRangeSuggestions, setShowRangeSuggestions] = useState(false);
    const [selectedRangeProduct, setSelectedRangeProduct] = useState(null);
    const rangeInputRef = useRef(null);
    const [rangeError, setRangeError] = useState("");
    const [rangeSaving, setRangeSaving] = useState(false);

    useEffect(() => {
        if (show && sourceProductId) {
            loadCurrentMapping();
            loadAllNewProducts();
            loadRanges();
        }
    }, [show, sourceProductId]);

    useEffect(() => {
        if (currentMapping && allNewProducts.length > 0) {
            const found = allNewProducts.find(np => np.id === currentMapping.newProductId);
            if (found) {
                setSelectedNewProduct(found);
                setSearchTerm(found.name);
            } else {
                setSelectedNewProduct({ id: currentMapping.newProductId, name: currentMapping.newProductName });
                setSearchTerm(currentMapping.newProductName || "");
            }
        } else if (!currentMapping) {
            setSelectedNewProduct(null);
            setSearchTerm("");
        }
    }, [currentMapping, allNewProducts]);

    const loadCurrentMapping = async () => {
        try {
            const response = await HttpNoData(
                `/api/v1/productmapping/by-source?sourceType=${sourceType}&sourceProductId=${sourceProductId}`,
                "GET",
                jwt
            );
            if (response.statusSuccessful) {
                setCurrentMapping(response.data);
            } else {
                setError("Не удалось загрузить привязку");
            }
        } catch {
            setError("Ошибка загрузки привязки");
        }
    };

    const loadAllNewProducts = async () => {
        try {
            const response = await HttpNoData("/api/v1/NewProducts/new", "GET", jwt);
            if (response.statusSuccessful) {
                setAllNewProducts(response.data || []);
            } else {
                setError("Не удалось загрузить список отправок");
            }
        } catch {
            setError("Ошибка загрузки списка отправок");
        }
    };

    const loadRanges = async () => {
        try {
            const data = await getProductSizeRangeMappingsBySource(sourceType, sourceProductId, jwt);
            setRanges(data || []);
        } catch {
            // не критично
        }
    };

    // --- основная привязка ---

    const filteredProducts = allNewProducts.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setShowSuggestions(true);
        if (selectedNewProduct && selectedNewProduct.name !== e.target.value) {
            setSelectedNewProduct(null);
        }
    };

    const handleSelectProduct = (product) => {
        setSelectedNewProduct(product);
        setSearchTerm(product.name);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleBlur = () => {
        setTimeout(() => setShowSuggestions(false), 150);
    };

    const saveMapping = async () => {
        if (!selectedNewProduct) return;
        setLoading(true);
        try {
            if (currentMapping) {
                const del = await HttpNoData(`/api/v1/productmapping/${currentMapping.id}`, "DELETE", jwt);
                if (!del.statusSuccessful) {
                    setError(del.error || "Ошибка при удалении старой привязки");
                    return;
                }
            }
            const create = await HttpData("/api/v1/productmapping", "POST", jwt, {
                newProductId: selectedNewProduct.id,
                sourceProductId,
                sourceType,
            });
            if (create.statusSuccessful) {
                await loadCurrentMapping();
                if (onMappingChanged) onMappingChanged();
                setError("");
            } else {
                setError(create.error || "Ошибка при создании связи");
                await loadCurrentMapping();
            }
        } catch {
            setError("Ошибка при сохранении связи");
        } finally {
            setLoading(false);
        }
    };

    const removeMapping = async () => {
        if (!currentMapping) return;
        if (!window.confirm("Удалить привязку?")) return;
        setLoading(true);
        try {
            const del = await HttpNoData(`/api/v1/productmapping/${currentMapping.id}`, "DELETE", jwt);
            if (del.statusSuccessful) {
                setCurrentMapping(null);
                setSelectedNewProduct(null);
                setSearchTerm("");
                if (onMappingChanged) onMappingChanged();
                setError("");
            } else {
                setError(del.error || "Ошибка при удалении");
            }
        } catch {
            setError("Ошибка при удалении");
        } finally {
            setLoading(false);
        }
    };

    // --- диапазоны ---

    const filteredRangeProducts = allNewProducts.filter(p =>
        p.name.toLowerCase().includes(rangeSearch.toLowerCase())
    );

    const handleSelectRangeProduct = (product) => {
        setSelectedRangeProduct(product);
        setRangeSearch(product.name);
        setShowRangeSuggestions(false);
        rangeInputRef.current?.focus();
    };

    const rangesOverlap = (sf, st) => {
        return ranges.some(r => {
            const existFrom = r.sizeFrom ?? -Infinity;
            const existTo = r.sizeTo ?? Infinity;
            const newFrom = sf ?? -Infinity;
            const newTo = st ?? Infinity;
            return newFrom < existTo && newTo > existFrom;
        });
    };

    const addRange = async () => {
        setRangeError("");
        const sf = rangeForm.sizeFrom !== "" ? parseFloat(rangeForm.sizeFrom) : null;
        const st = rangeForm.sizeTo !== "" ? parseFloat(rangeForm.sizeTo) : null;

        if (sf !== null && isNaN(sf)) return setRangeError("SizeFrom должен быть числом");
        if (st !== null && isNaN(st)) return setRangeError("SizeTo должен быть числом");
        if (sf !== null && st !== null && sf >= st) return setRangeError("SizeFrom должен быть меньше SizeTo");
        if (!selectedRangeProduct) return setRangeError("Выберите продукт отправок");
        if (rangesOverlap(sf, st)) return setRangeError("Диапазон пересекается с существующим");

        setRangeSaving(true);
        try {
            const result = await createProductSizeRangeMapping({
                sourceProductId,
                sourceType,
                sizeFrom: sf,
                sizeTo: st,
                newProductId: selectedRangeProduct.id,
            }, jwt);

            if (result.statusSuccessful) {
                await loadRanges();
                setShowAddRange(false);
                setRangeForm({ sizeFrom: "", sizeTo: "", newProductId: null });
                setRangeSearch("");
                setSelectedRangeProduct(null);
            } else {
                setRangeError(result.error || "Ошибка при добавлении диапазона");
            }
        } catch {
            setRangeError("Ошибка при добавлении диапазона");
        } finally {
            setRangeSaving(false);
        }
    };

    const removeRange = async (id) => {
        if (!window.confirm("Удалить диапазон?")) return;
        try {
            await deleteProductSizeRangeMapping(id, jwt);
            await loadRanges();
        } catch {
            setError("Ошибка при удалении диапазона");
        }
    };

    const formatBound = (v) => (v == null ? "∞" : v);

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Привязка к отправке</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {/* Диапазоны размеров */}
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <strong>Диапазоны размеров</strong>
                        {ranges.length > 0 && (
                            <Badge bg="info" className="ms-2">{ranges.length}/6</Badge>
                        )}
                        {ranges.length < 6 && !showAddRange && (
                            <Button size="sm" variant="outline-primary" onClick={() => setShowAddRange(true)}>
                                + Добавить диапазон
                            </Button>
                        )}
                    </div>

                    {ranges.length > 0 && (
                        <Alert variant="info" className="py-2 mb-2" style={{ fontSize: "0.85em" }}>
                            При экспорте будут использоваться диапазоны вместо простой привязки
                        </Alert>
                    )}

                    {ranges.length > 0 && (
                        <Table size="sm" bordered className="mb-2">
                            <thead>
                                <tr>
                                    <th>От (мм)</th>
                                    <th>До (мм)</th>
                                    <th>Продукт отправок</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {ranges.map(r => (
                                    <tr key={r.id}>
                                        <td>{formatBound(r.sizeFrom)}</td>
                                        <td>{formatBound(r.sizeTo)}</td>
                                        <td>{r.newProductName} <span className="text-muted small">(ID: {r.newProductId})</span></td>
                                        <td>
                                            <Button size="sm" variant="outline-danger" onClick={() => removeRange(r.id)}>
                                                ✕
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    {ranges.length === 0 && !showAddRange && (
                        <div className="text-muted small">Диапазоны не заданы — используется простая привязка</div>
                    )}

                    {showAddRange && (
                        <div className="border rounded p-3 mb-2 bg-light">
                            <div className="row g-2 mb-2">
                                <div className="col">
                                    <Form.Label className="small mb-1">От (мм, необяз.)</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        placeholder="нет границы"
                                        value={rangeForm.sizeFrom}
                                        onChange={e => setRangeForm(f => ({ ...f, sizeFrom: e.target.value }))}
                                    />
                                </div>
                                <div className="col">
                                    <Form.Label className="small mb-1">До (мм, необяз.)</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        placeholder="нет границы"
                                        value={rangeForm.sizeTo}
                                        onChange={e => setRangeForm(f => ({ ...f, sizeTo: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <Form.Label className="small mb-1">Продукт отправок</Form.Label>
                            <div style={{ position: "relative" }} className="mb-2">
                                <Form.Control
                                    ref={rangeInputRef}
                                    size="sm"
                                    type="text"
                                    placeholder="Введите название для поиска"
                                    value={rangeSearch}
                                    onChange={e => {
                                        setRangeSearch(e.target.value);
                                        setShowRangeSuggestions(true);
                                        if (selectedRangeProduct && selectedRangeProduct.name !== e.target.value)
                                            setSelectedRangeProduct(null);
                                    }}
                                    onFocus={() => setShowRangeSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowRangeSuggestions(false), 150)}
                                    autoComplete="off"
                                />
                                {showRangeSuggestions && filteredRangeProducts.length > 0 && (
                                    <div style={{
                                        position: "absolute", zIndex: 1000, width: "100%",
                                        maxHeight: "180px", overflowY: "auto",
                                        backgroundColor: "white", border: "1px solid #ced4da",
                                        borderRadius: "0.25rem", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", marginTop: "2px"
                                    }}>
                                        {filteredRangeProducts.map(p => (
                                            <div key={p.id} onClick={() => handleSelectRangeProduct(p)}
                                                style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                                                onMouseEnter={e => e.target.style.backgroundColor = "#f8f9fa"}
                                                onMouseLeave={e => e.target.style.backgroundColor = "white"}>
                                                {p.name} (ID: {p.id})
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {rangeError && <Alert variant="danger" className="py-1 mb-2" style={{ fontSize: "0.85em" }}>{rangeError}</Alert>}
                            <Stack direction="horizontal" gap={2}>
                                <Button size="sm" variant="success" onClick={addRange} disabled={rangeSaving}>
                                    Добавить
                                </Button>
                                <Button size="sm" variant="secondary" onClick={() => {
                                    setShowAddRange(false);
                                    setRangeError("");
                                    setRangeForm({ sizeFrom: "", sizeTo: "", newProductId: null });
                                    setRangeSearch("");
                                    setSelectedRangeProduct(null);
                                }}>
                                    Отмена
                                </Button>
                            </Stack>
                        </div>
                    )}
                </div>

                <hr />

                {/* Основная привязка */}
                <Form.Group className="mb-3">
                    <Form.Label>Простая привязка</Form.Label>
                    <div style={{ position: "relative" }}>
                        <Form.Control
                            ref={inputRef}
                            type="text"
                            placeholder="Введите название для поиска"
                            value={searchTerm}
                            onChange={handleInputChange}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={handleBlur}
                            disabled={loading}
                            autoComplete="off"
                        />
                        {showSuggestions && filteredProducts.length > 0 && (
                            <div
                                ref={suggestionsRef}
                                style={{
                                    position: "absolute", zIndex: 1000, width: "100%",
                                    maxHeight: "200px", overflowY: "auto",
                                    backgroundColor: "white", border: "1px solid #ced4da",
                                    borderRadius: "0.25rem", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", marginTop: "2px"
                                }}
                            >
                                {filteredProducts.map(product => (
                                    <div key={product.id} onClick={() => handleSelectProduct(product)}
                                        style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                                        onMouseEnter={e => e.target.style.backgroundColor = "#f8f9fa"}
                                        onMouseLeave={e => e.target.style.backgroundColor = "white"}>
                                        {product.name} (ID: {product.id})
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedNewProduct && (
                        <Form.Text className="text-muted">
                            Выбрано: {selectedNewProduct.name} (ID: {selectedNewProduct.id})
                        </Form.Text>
                    )}
                </Form.Group>

                <Stack direction="horizontal" gap={2} className="justify-content-end">
                    {currentMapping && (
                        <Button variant="danger" onClick={removeMapping} disabled={loading}>
                            Удалить привязку
                        </Button>
                    )}
                    <Button variant="success" onClick={saveMapping} disabled={!selectedNewProduct || loading}>
                        {currentMapping ? "Изменить" : "Привязать"}
                    </Button>
                </Stack>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Закрыть
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
